<template>
  <el-dialog
    class="dialog-wrapper change-dilution"
    :visible="true"
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        $t('analysesData.changeSamplingInformation.title', {
          sample: sample.labSampleCode,
        })
      "
    />
    <ValidationObserver
      ref="operationForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="12">
            <FormItem
              :label="$t('analysesData.changeSamplingInformation.samplingDate')"
            >
              <el-date-picker
                v-model="formData.date"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder=""
                :picker-options="{ disabledDate: isStartDateDisabled }"
              />
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem
              :label="
                $t('analysesData.changeSamplingInformation.samplingPlace')
              "
            >
              <el-input v-model="formData.place"></el-input>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="12">
            <FormItem
              :label="$t('analysesData.changeSamplingInformation.samplingPlan')"
            >
              <el-input v-model="formData.plan"></el-input>
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem
              :label="
                $t('analysesData.changeSamplingInformation.samplingMethod')
              "
            >
              <el-input v-model="formData.method"></el-input>
            </FormItem>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <FormItem
              :label="$t('analysesData.changeSamplingInformation.samplingBy')"
            >
              <el-select
                v-model="formData.sampledBy"
                placeholder="Select"
              >
                <el-option
                  v-for="item in sampledBy"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="12">
            <FormItem
              :label="
                $t(
                  'analysesData.changeSamplingInformation.samplingAccredited.label'
                )
              "
            >
              <el-switch
                v-model="formData.accredited"
                :active-text="
                  $t(
                    'analysesData.changeSamplingInformation.samplingAccredited.active'
                  )
                "
                :inactive-text="
                  $t(
                    'analysesData.changeSamplingInformation.samplingAccredited.inactive'
                  )
                "
                :active-value="true"
                :inactive-value="false"
              >
              </el-switch>
            </FormItem>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('global.cancel') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(patchSamplingInfo)"
          >
            {{ $t('analysesData.changeSamplingInformation.update') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import { SampledBy } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';

  import { samplesApi } from '@/api';

  const defaultData = {
    date: null,
    place: null,
    plan: null,
    method: null,
    sampledBy: SampledBy.Customer,
    accredited: null,
  };

  export default {
    name: 'ChangeSamplingInformation',
    components: {
      ContentHeader,
      FormButtons,
      FormItem,
    },
    props: {
      samples: { required: true },
    },
    data() {
      return {
        formData: {
          ...defaultData,
        },
      };
    },
    computed: {
      sample() {
        return this.samples.length ? this.samples[0] : {};
      },
      sampledBy() {
        return Object.values(SampledBy).map((val) => ({
          value: val,
          label: this.$t(`enums.sampledBy.${val}`),
        }));
      },
    },
    created() {
      if (this.sample.samplingInfo) {
        const { date, method, place, plan, sampledBy, accredited } =
          this.sample.samplingInfo;
        this.formData.date = date || null;
        this.formData.method = method;
        this.formData.place = place;
        this.formData.plan = plan;
        this.formData.sampledBy = sampledBy || SampledBy.Customer;
        this.formData.accredited = accredited || null;
      }
    },
    methods: {
      abortAction() {
        this.$emit('dialogClose', { success: false });
      },
      async patchSamplingInfo() {
        try {
          await samplesApi.patchSamplingInfo(this.sample.id, {
            ...this.formData,
            date: this.formData.date
              ? new Date(this.formData.date).toISOString()
              : null,
          });
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      isStartDateDisabled(date) {
        const today = moment.utc().startOf('day').toDate();
        if (date > today) return true;

        if (this.sample.acceptedAt) {
          const acceptedAt = moment(this.sample.acceptedAt)
            .startOf('day')
            .toDate();
          if (date > acceptedAt) return true;
        }
        return false;
      },
    },
  };
</script>
<style lang="scss">
  .change-dilution {
    .el-dialog {
      width: 50%;
    }
  }
</style>
